import type { JSONSchemaFieldType } from '@witkac/jschema';

import type { IMaterialSymbolConfig } from '../interfaces/materialSymbolConfig';
import type { AlignType } from '../typings/alignType';
import type { BreakpointType } from '../typings/breakpointType';
import type { ButtonGroupVariantType } from '../typings/buttonGroupVariantType';
import type { ButtonVariantType } from '../typings/buttonVariantType';
import type { ChipVariantType } from '../typings/chipVariantType';
import type { ColorType } from '../typings/colorType';
import type { ColSizeType } from '../typings/colSizeType';
import type { DirectionType } from '../typings/directionType';
import type { DisabledVariantType } from '../typings/disabledVariantType';
import type { FieldListVariantType } from '../typings/fieldListVariantType';
import type { FieldPositionType } from '../typings/fieldPositionType';
import type { FieldVariantType } from '../typings/fieldVariantType';
import type { FileInputVariantType } from '../typings/fileInputVariantType';
import type { GapType } from '../typings/gapType';
import type { IconFamilyType } from '../typings/iconFamilyType';
import type { IconPositionType } from '../typings/iconPositionType';
import type { InputDescriptionVariantType } from '../typings/inputDescriptionVariantType';
import type { InputVariantType } from '../typings/inputVariantType';
import type { JustifyType } from '../typings/justifyType';
import type { MessageVariantType } from '../typings/messageVariantType';
import type { NotificationVariantType } from '../typings/notificationVariantType';
import type { OptionsListVariantType } from '../typings/optionsListVariant';
import type { OrientationType } from '../typings/orientationType';
import type { RadiusType } from '../typings/radiusType';
import type { RequirementVariantType } from '../typings/requirementVariantType';
import type { RouterActivityType } from '../typings/routerActivityType';
import type { SizeType, SizeBreakpointType, ISize } from '../typings/sizeType';
import type { TableCellSeparatorType } from '../typings/tableCellSeparatorType';
import type { TableVariantType } from '../typings/tableVariantType';
import type { TextAlignType } from '../typings/textAlignType';
import type { ToastPositionType } from '../typings/toastPosition';
import type { ToolbarPositionType } from '../typings/toolbarPositionType';
import type { WrapType } from '../typings/wrapType';

export const defaults = {
  /** Domyślna wielkość używana w systemie*/
  size: <SizeType>'md',

  button: {
    /** Domyślny wariant stylistyczny przycisku */
    variant: <ButtonVariantType>'filled',

    /** Domyślne rozciąganie przycisku na całą dostępną szerokość */
    fluid: <boolean>false,

    /** Domyślny kolor przycisku */
    color: <ColorType | undefined>undefined,

    /** Domyślna pozycja ikony w przycisku*/
    iconPosition: <IconPositionType>'left',

    /** Domyślne justowanie kontentu w przycisku */
    justify: <JustifyType>'start',

    /** Domyślne justowanie tekstu w przycisku */
    textAlign: <TextAlignType>'center',

    /** Domyślna liczba wierszy tekstu wyświetlana w przycisku przed ucięciem*/
    textClip: <number>0,

    /** Domyślny rozmiar przycisku */
    size: <SizeType>'md',

    /** Wielkości ikon zależnie od wielkości przycisku*/
    iconSize: <ISize>{
      xs: 'xs',
      sm: 'sm',
      md: 'md',
      lg: 'md',
      xl: 'lg'
    },

    /** Domyślny atrybut "Type" przycisku */
    type: <string>'button',

    /** Domyślne oznaczanie przycisku jako aktywny gdy kieruje on na widok, na którym się znajduje */
    routerActivity: <RouterActivityType | undefined>undefined
  },

  buttonGroup: {
    /** Domyślny Wariant stylistyczny grupy przycisków */
    variant: <ButtonGroupVariantType>'filled',

    /** Domyślne odstępy pomiędzy przyciskami w grupie */
    space: <GapType>4,

    /** Czy łączyć ze sobą przyciski gdy space = 0 */
    connect: <boolean>true
  },

  inputs: {
    /** Domyślny wariant stylistyczny inputów */
    variant: <InputVariantType>'filled',
    disabledVariant: <DisabledVariantType>'hidden',
    textAlign: <TextAlignType>'left',

    description: {
      /** Domyślny wariant stylistyczny opisu inputów */
      variant: <InputDescriptionVariantType>'default',

      /** Domyślna ikona opisu inputów */
      icon: <string>'question_mark',

      /** Domyślna ikona wyświetlana jako target tooltipa po najechaniu której pokazuje się opis */
      tooltipIcon: <string>'help',

      /** Domyślny kolor tooltipa opisu inputów*/
      tooltipColor: <ColorType>'info'
    }
  },

  terc: {
    /** Domyślny odstęp pomiędzy polami terc*/
    gap: <GapType | undefined>undefined,

    province: {
      /** Domyślna wielkość kolumny województwa*/
      size: <ColSizeType>12
    },

    district: {
      /** Domyślna wielkość kolumny powiatu */
      size: <ColSizeType>12
    },

    commune: {
      /** Domyślna wielkość kolumny gminy */
      size: <ColSizeType>12
    }
  },

  grid: {
    /** Domyślne odstępy pomiędzy elementami gridu */
    gap: <GapType | undefined>undefined,

    row: {
      /** Domyślny tag wiersza */
      tag: <string>'div',

      /** Domyślny alignment elementów wewnątrz wiersza */
      align: <AlignType>'start'
    },

    col: {
      /** Domyślny tag kolumny */
      tag: <string>'div',

      /** Domyślne justowanie elementów wewnątrz kolumny */
      justify: <JustifyType>'start',

      /** Domyślny alignment elementów wewnątrz kolumny */
      align: <AlignType>'stretch',

      /** Domyślny odstęp elementów wewnątrz kolumny */
      gap: <GapType | undefined>undefined,

      /** Czy domyślnie wrapować elementy wewnątrz kolumny do nowej linii, gdy nie mieszczą się w szerokości */
      wrap: <WrapType>'nowrap'
    }
  },

  textarea: {
    /** Czy textarea jest możliwa do resizowania */
    resizable: <boolean>false,

    /** Czy textarea powiększa się wraz z wpisywanym tekstem */
    autoExpand: <boolean>true,

    /** Domyślna wysokość textarea */
    height: <string>'100px',

    /** Domyślna maksymalna wysokość textarea */
    maxHeight: <string>'300px',

    /** Czy używać w-scroll w komponencie */
    customScroll: <boolean>true
  },

  wysiwyg: {
    /** Domyślna wysokość wysiwyga */
    height: <string>'120px',

    /** Domyślna pozycja paska narzędziowego */
    toolbarPosition: <ToolbarPositionType>'bottom',

    /** Domyślne ustawienie czy pasek narzędziowy ma znikać po wyjściu z pola */
    dynamicToolbar: <boolean>true
  },

  chip: {
    /** Domyślny tag chipsów */
    tag: <string>'div',

    /** Domyślny rozmiar chipsa */
    size: <SizeType>'md',

    /** Domyślny wariant chipsów */
    variant: <ChipVariantType>'filled',

    /** Domyślny kolor chipsów */
    color: <ColorType | undefined>undefined,

    /** Domyślne justowanie tekstu w chipsach */
    textAlign: <TextAlignType>'left',

    /** Domyślny rozmiar ikony w chipsie */
    iconSize: <SizeType>'sm',

    /** Domyślna liczba wierszy wyświetlana w chipsie */
    textClip: <number>1,

    clearableButton: {
      /** Wariant przycisku czyszczenia w chipsach */
      variant: <ButtonVariantType>'filled',

      /** Kolor przycisku czyszczenia w chipsach */
      color: <ColorType>'danger',

      /** Rozmiar przycisku czyszczenia w chipsach */
      size: <SizeType>'xs',

      /** Zaokrąglenie przycisku czyszczenia w chipsach */
      radius: <RadiusType>'rounded'
    }
  },

  select: {
    /** Domyślny wariant listy opcji */
    optionsListVariant: <OptionsListVariantType>'tree',

    /** Domyślna wielkość opcji */
    optionsSize: <SizeType | SizeBreakpointType>{ xs: 'lg', md: 'md' },

    /** Domyślna wielkość opcji */
    optionsColor: <ColorType>'neutral',

    /** Domyślny tekst wyświetlany gdy lista selekta jest pusta */
    emptyListText: <string>'Brak opcji do wyświetlenia',

    /** Domyślny tekst wyświetlany gdy wyświetlono już wszystkie dostępne opcje */
    noMoreOptionsText: <string>'Nie znaleziono więcej opcji',

    /** Domyślny tekst sugerujący możliwość wyszukiwania */
    startSearchingText: <string>'Zacznij wpisywać aby wyszukać',

    /** Domyślna maksymalna liczba wyników na liście */
    listLength: <number | undefined>undefined,

    /** Domyślna szerokość listy */
    /** TODO: Ujednolicić na stringi ale zrobić tak aby działały dalej usePosition */
    listWidth: <number>400,

    /** Domyślna wysokość listy */
    /** TODO: Ujednolicić na stringi ale zrobić tak aby działały dalej usePosition */
    listHeight: <number>300,

    /** Czy używać w-scroll w komponencie */
    customScroll: <boolean>true,

    /** Domyślny breakpoint od którego lista wyświetla się w trybie mobilnym */
    mobileBreakpoint: <BreakpointType | undefined>'sm',

    chips: {
      /** Domyślne zaokrąglenie chipsów w selekcie*/
      radius: <RadiusType | undefined>undefined
    }
  },

  datePicker: {
    /** Domyślny kolor elementów w datepickerze (Kolory brane z v-calendar, nie z w-colors) */
    color: <string>'blue',

    /** Domyślna ikona w datepickerze */
    icon: <string | undefined>'calendar_today',

    /** Domyślna ikona w daterangepickerze */
    rangeIcon: <string | undefined>'date_range'
  },

  fileInput: {
    /** Domyślny wariant */
    variant: <FileInputVariantType>'outlined',

    /** Domyślne akceptowane formaty */
    acceptText: <string>'.pdf,.jpg,.jpeg,.png',

    /** Domyślny tekst wyświetlany domyślnie w komponencie */
    dragOrClickText: <string>'Kliknij lub przeciągnij plik tutaj',

    /** Domyślny tekst wyświetlany podczas przeciągania pliku na komponent */
    dropHereText: <string>'Upuść plik tutaj',

    /** Domyślny tekst przycisku usuwania wgranych plików */
    deleteText: <string>'Usuń',

    /** Domyślny tekst wyświetlany gdy przycisk jest nieaktywny */
    disabledText: <string>'Dodawanie plików jest zablokowane',

    /** Domyślny tekst wyświetlany gdy osiągnięto maksymalną liczbę przesłanych plików*/
    maxFilesText: <string>'Osiągnięto maksymalną liczbę plików',

    /** Domyślny tekst wyświetlany gdy osiągnięto maksymalną liczbę przesłanych plików*/
    uploadedFilesText: <string>'Przesłane pliki'
  },

  phoneInput: {
    codeSelect: {
      /** Szerokość selecta z numerem kierunkowym */
      width: <string>'100px',

      /** Szerokość listy z numerami kierunkowmi */
      listWidth: <string>'360px'
    }
  },

  fieldList: {
    /** Domyślny wariant stylistyczny Radio/Checkbox list*/
    variant: <FieldListVariantType>'outlined',

    /** Domyślne rozciąganie list na pełną dostępną szerokość*/
    fluid: <boolean>true,

    /** Domyślny justowanie flex w listach Radio/Checkbox list */
    justify: <JustifyType>'start',

    /** Domyślny align flex w listach Radio/Checkbox list */
    align: <AlignType>'start',

    /** Domyślna orientacja Radio/Checkbox list*/
    orientation: <OrientationType>'vertical'
  },

  fields: {
    /** Domyślny tag pól (Radio/Checkbox) */
    tag: <string>'div',

    /** Domyślny wariant stylistyczny pól (Radio/Checkbox) */
    variant: <FieldVariantType>'default',

    /** Domyślna pozycja pola (Radio/Checkbox)*/
    position: <FieldPositionType>'left',

    /** Domyślny kolor pola*/
    color: <ColorType>'primary'
  },

  switch: {
    /** Domyślna konfiguracja ikon w switchu */
    icon: {
      config: <IMaterialSymbolConfig>{
        weight: 700,
        grade: 0,
        opticalSize: 20,
        fill: true
      }
    }
  },

  flex: {
    /** Domyślin tag elementów flex*/
    tag: <string>'div',

    /** Domyślny kierunek układania elementów flex */
    direction: <DirectionType>'row',

    /** Domyślne rozciąganie kontenera na całą dostępną szerokość*/
    fluid: <boolean>false,

    /** Czy przerzucać elementy do nowej linii gdy nie mieszczą się w kontenerze*/
    wrap: <WrapType>'nowrap',

    /** Domyślne justowanie elementów flex*/
    justify: <JustifyType>'start',

    /** Domyślny alignment elementów flex*/
    align: <AlignType>'start',

    /** Domyslne odstępy pomiędzy elementami flex*/
    gap: <GapType | undefined>undefined
  },

  modal: {
    /** Czy rozmazywać tło za modalem */
    blur: <boolean>false,

    /** Domyślny kolor modala */
    color: <ColorType | undefined>undefined,

    /** Tekst przycisku zamykania modala */
    closeLabel: <string>'Zamknij',

    /** Czy domyślnie korzystać z w-scroll w modalu */
    customScroll: <boolean>true,

    /** Czy domyślnie przyklejać nagłówek modala do krawędzi ekranu podczas scrolla */
    stickyHeader: <boolean>true,

    /** Czy domyślnie przyklejać stopkę modala do krawędzi ekranu podczas scrolla */
    stickyFooter: <boolean>true
  },

  slidingPanel: {
    /** Domyślna wielkość sliding-panelu*/
    size: <string>'400px',

    /** Domyślny kolor panelu */
    color: <ColorType | undefined>undefined,

    /** Czy domyślnie korzystać z w-scroll w sliding panelu */
    customScroll: <boolean>true
  },

  tooltip: {
    /** Domyślne opóźnienie pojawiania się tooltipa */
    delay: <number>0,

    /** Domyślny kolor tooltipa */
    color: <ColorType | undefined>undefined,

    /** Domyślne rozciąganie targetu tooltipa */
    fluid: <boolean>false
  },

  dropdown: {
    /** Domyślna szerokość dropdowna */
    /** TODO: Ujednolicić na stringi ale zrobić tak aby działały dalej usePosition */
    width: <number>400,

    /** Domyślna wysokość dropdowna */
    /** TODO: Ujednolicić na stringi ale zrobić tak aby działały dalej usePosition */
    height: <number>200,

    /** Domyślne rozciąganie dropdowna na pełną szerokość targetu */
    fluid: <boolean>false,

    /** Czy wyświetlać pełnoekranową maskę po otwarciu dropdowna */
    mask: <boolean>false,

    /** Czy używać w-scroll w komponencie*/
    customScroll: <boolean>true,

    /** Domyślny breakpoint od którego dropdown przechodzi w tryb mobilny*/
    mobileBreakpoint: <BreakpointType>'sm'
  },

  skeleton: {
    /** Domyślna orientacja animacji skeletona */
    orientation: <OrientationType>'horizontal',

    /** Domyślna szerokość skeletona */
    width: <string>'100%',

    /** Domyślna wysokość skeletona */
    height: <string>'24px',

    /** Domyślna przezroczystość skeletona*/
    intensity: <number>0.5
  },

  label: {
    /** Domyślny tag labelu*/
    tag: <string>'label',

    /** Domyślny wariant wyświetlania oznazceń wymagalności*/
    requirementVariant: <RequirementVariantType>'optional',

    /** Domyślny tekst opcjonalnego pola */
    optionalText: <string>'(Opcjonalne)',

    /** Domyślny tekst wymaganego pola */
    requiredText: <string>' *'
  },

  schema: {
    /** Czy wyświetlać walidację pola jako wypełnione poprawnie */
    displaySuccess: <boolean>false,

    /** Domyślny typ modelu */
    defaultModelType: <JSONSchemaFieldType>'string'
  },

  counter: {
    /** Domyślny tekst licznika w inputach */
    text: <string>'Liczba znaków: ',

    /** Domyślna ikona licznika */
    icon: <string>'text_fields_alt',

    /** Czy wyświetlać licznik w inputcie */
    displayCounter: <boolean>true
  },

  message: {
    /** Kolor komunikatu */
    color: <ColorType>'neutral',

    /** Czy domyślnie pojawiać komunikat z dynamiczną wysokością */
    dynamic: <boolean>false,

    /** Domyślny wariant stylistyczny komunikatu */
    variant: <MessageVariantType>'simple',

    /** Domyślna wielkość chipsa z ikoną */
    chipSize: <SizeType>'sm',

    icon: {
      /** Domyślna ikona powiadomienia */
      icon: <string | undefined>'question_mark',

      /** Wielkość ikony */
      size: <SizeType>'xs',

      /** domyślny wariant chipsa ikony */
      defaultChipVariant: <ChipVariantType>'outlined',

      /** Wariant chipsa ikony jako tooltip*/
      tooltipChipVariant: <ChipVariantType>'elevated',

      /** Domyślna konfiguracja ikony komunikatu */
      config: <IMaterialSymbolConfig>{
        weight: 700,
        grade: -25,
        opticalSize: 20,
        fill: false
      }
    }
  },

  inputMessage: {
    /** Domyślna stylistyka powiadomień walidacyjnych w inputach */
    variant: <InputVariantType>'dynamic',

    /** Czy wyświetlać tekst w powiadomieniu walidacyjnym */
    displayText: <boolean>true,

    /** Czy wyświetlać ikonę w powiadomieniu walidacyjnym */
    displayIcon: <boolean>true,

    /** Treść komunikatu sukcesu */
    successText: 'Wypełniono poprawnie',

    icons: {
      /** Domyślna ikona walidacyjna błędu */
      error: <string>'priority_high',

      /** Domyślna ikona walidacyjna ostrzeżenia */
      warning: <string>'priority_high',

      /** Domyślna ikona opisu */
      description: <string>'question_mark',

      /** Domyślna ikona walidacyjna sukcesu */
      success: <string>'check'
    }
  },

  pagination: {
    /** Domyślna liczba wyświetlanych wyników na stronie w paginacji */
    resultsPerPage: <number>50,

    /** Domyślna wielkość elementów paginacji */
    size: <SizeType>'sm',

    /** Wielkość opcji w selekcie ilości wyników */
    optionsSize: <SizeType | SizeBreakpointType>{ xs: 'lg', md: 'xs' },

    /** Wariant przycisków paginacji */
    buttonVariant: <ButtonVariantType>'ghost',

    /** Kolor przycisku aktywnej strony paginacji */
    buttonColor: <ColorType | undefined>undefined,

    /** Wariant przycisku aktywnej strony paginacji */
    activeButtonVariant: <ButtonVariantType>'tonal',

    /** Kolor przycisku aktywnej strony paginacji */
    activeButtonColor: <ColorType>'primary',

    /** Szerokość dropdownów do wyborów strony i ilości wyników */
    morePagesDropdownWidth: <string>'60px',

    resultsSelect: {
      /** Wariant selekta od wyboru ilości wyników na stronie */
      variant: <InputVariantType>'filled',

      /** Tekst wyświetlany przed selektem wyboru ilości wyników */
      text: <string>'Pokaż na stronie:',

      /** Szerokość selekta od ilości wyników*/
      width: <string>'84px',

      /** Szerokość dropdownów do wyborów strony i ilości wyników */
      dropdownWidth: <string>'84px'
    },

    /** Tekst wyświetlany przed ilością stron*/
    pagesText: <string>'Strona:',

    /** Symbol wyświetlany pomiędzy ilością stron*/
    resultsSeparatorSymbol: <string>'-',

    /** Tekst wyświetlany pomiędzy wyświetlanymi wynikami a wszystkimi wynikami*/
    resultsFromText: <string>'z',

    /** Tekst wyświetlany po ilości wyników*/
    resultsText: <string>'Wyników',

    /** Teksty wyświetlany gdy brak wyników*/
    noResultsText: <string>'- Brak wyników -',

    /** Breakpoint od którego paginacja przechodzi w tryb mobilny */
    mobileBreakpoint: <BreakpointType>'sm',

    /** Wartości używane do selecta służącego zmianie liczbie elementów na stronie */
    perPageItems: <number[]>[10, 25, 50, 100],

    /** Domyślna maksymalna licza stron, która może być wyświetlane na liście stron, zanim pojawi się opcja wybrania dowolnej (najlepiej nieparzysta)*/
    collapsePagesAmount: <number>5
  },

  table: {
    /** Domyślny tryb responsywny tabeli*/
    responsive: <boolean>true,

    /** Domyślny wariant stylistyczny tabeli*/
    variant: <TableVariantType>'table',

    /** Domyślne separatory tabeli*/
    separator: <TableCellSeparatorType | undefined>undefined,

    /** Czy wyświetlać paddingi na krawędziach tabeli */
    edgePadding: <boolean>true,

    /** Breakpoint od którego tabela przejdzie w tryb mobilny */
    wrapBreakpoint: <BreakpointType | undefined>undefined,

    cell: {
      /** Domyślna szerokość kolumny */
      width: <string | number>'auto',

      /** Domyślny alignment wewnątrz komórki */
      align: <AlignType>'start',

      /** Domyślne justowanie wewnątrz komórki */
      justify: <JustifyType>'start',

      /** Domyślne justowanie tekstu wewnątrz komórki */
      textAlign: <TextAlignType>'left',

      /** Domyślny kolor komórki */
      color: <ColorType | undefined>undefined,

      selectable: {
        /** Domyślna wielkość checkboxa od zaznaczania komórki*/
        size: <SizeType>'sm',

        /** Domyślny kolor checkboxa od zaznaczania komórki*/
        color: <ColorType>'primary',

        /** Domyślny tekst przycisku do zaznaczania wszystkich widocznych opcji w tabeli*/
        selectAllText: <string>'Zaznacz widoczne',

        /** Domyślny tekst przycisku do zaznaczania wszystkich widocznych opcji w tabeli ze wszystkich stron*/
        selectAllPagesText: <string>'Zaznacz wszystkie strony',

        /** Domyślny tekst przycisku do odznaczania wszystkich opcji w tabeli ze wszystkich stron*/
        deselectAllText: <string>'Odznacz wszystkie'
      }
    }
  },

  avatar: {
    /** Domyślny tag awatara */
    tag: <string>'div',

    /** Domyślna ikona gdy nie występuja awatar */
    placeholderIcon: <string>'person',

    /** Wartość o którą zmniejszany jest font wraz z ilością znaków */
    fontScaleMultipler: <number>0.1,

    /** Minimalna wielkość fontu do jakiej może zostać zmniejszony */
    minFontSize: <number>0.6
  },

  avatarGroup: {
    /** Domyślna maksymalna liczba awatarów w grupie */
    max: <number>4,

    /** Czy domyślnie wrapować awatary do nowej linii, gdy nie mieszczą się w szerokośći */
    wrap: <WrapType>'wrap',

    /** Domyślna wartość ujemnych odstępów awatarów w osi X */
    negativeSpaceX: <number>16,

    /** Domyślna wartość ujemnych odstępów awatarów w osi Y */
    negativeSpaceY: <number>4
  },

  notification: {
    /** Domyślny wariant notyfikacji */
    variant: <NotificationVariantType>'neutral',

    /** Czas wyświetlania na ekranie (w milisekundach)*/
    displayTime: <number>4800,

    /** Czas trwania animacji zamykania toasta (w milisekundach)
     * Potrzebny do wyliczenia czasu renderowania komponentu
     */
    closingAnimationTime: <number>700,

    toast: {
      /** Domyślna pozycja notyfikacji toast */
      position: <ToastPositionType>'bottom',

      /** Domyślna szerokość toastów */
      width: <string>'440px',

      closeButton: {
        /** Wariant stylistyczny przycisku zamykania*/
        variant: <ButtonVariantType>'ghost',

        /** Wielkość przycisku zamykania*/
        size: <SizeType>'sm',

        /** Kolor przycisku zamykania*/
        color: <ColorType | undefined>undefined,

        /** Label przycisku zamykania */
        label: <string>'Zamknij'
      }
    },

    icons: {
      /** Domyślna ikona komunikatu neutralnego */
      neutral: <string>'notifications_active',

      /** Domyślna ikona komunikatu informacyjnego */
      info: <string>'help',

      /** Domyślna ikona komunikatu błędu */
      danger: <string>'error',

      /** Domyślna ikona komunikatu ostrzeżenia */
      caution: <string>'exclamation',

      /** Domyślna ikona komunikatu sukcesu */
      success: <string>'check_circle'
    }
  },

  displays: {
    schema: {
      /** Domyślny tag tekstu*/
      tag: <string>'p',

      /** Domyślny kierunek justowania tekstu*/
      textAlign: <TextAlignType>'left',

      /** Domyślna liczba wierszy po których tekst będzie ucinany */
      textClip: <number>0,

      /** Czy display domyślnie wyświetla się na pełnej szerokości */
      fluid: <boolean>true,

      /** Domyślne justowanie flex elementu*/
      justify: <JustifyType>'stretch',

      /** Czy domyślnie wyświetlać walidację schemy*/
      displayValidation: <boolean>false
    }
  },

  icons: {
    /** Domyślna rodzina ikon */
    family: <IconFamilyType>'ms-r',

    /** Domyślna wielkość ikon */
    size: <SizeType>'md',

    config: <IMaterialSymbolConfig>{
      weight: 300,
      grade: 0,
      opticalSize: 24,
      fill: false
    },

    list: {
      /** Ikona wyszukiwania autocomplete*/
      autocompleteSearch: <string>'search',

      /** Ikona zamykania dropdowna w trybie mobilnym*/
      mobileDropdownClose: <string>'close',

      /** Strzałka w selekcie */
      selectDropDown: <string>'arrow_drop_down',

      /** Domyślna ikona zamykania */
      close: <string>'close',

      /** Domyślna ikona czyszczenia wartości */
      clearable: <string>'backspace',

      /** Domyślna ikona możliwości sortowania */
      sortable: <string>'unfold_more',

      /** Domyślna ikona sortowania rosnąco*/
      sortAscending: <string>'arrow_downward_alt',

      /** Domyślna ikona sortowania malejąco*/
      sortDescending: <string>'arrow_upward_alt',

      /** Ikona zaznaczania wszystkich wyników*/
      selectAll: <string>'check_box',

      /** Ikona zaznaczania wszystkich na wszystkich stronach*/
      selectAllPages: <string>'library_add_check',

      /** Ikona odznaczania wszystkich*/
      deselectAllPages: <string>'indeterminate_check_box',

      /** Ikona poprzedniej strony */
      prevPage: <string>'chevron_left',

      /** Ikona następnej strony */
      nextPage: <string>'chevron_right',

      /** Ikona pozostałych stron */
      morePages: <string>'more_horiz',

      /** Ikona możliwości uploadu*/
      upload: <string>'cloud_upload',

      /** Ikona braku możliwości uploadu*/
      uploadDisabled: <string>'file_upload_off',

      /** Ikona osiągnięcia maksymalnej ilości plików przy uploadzie*/
      uploadMaxFiles: <string>'cloud_done',

      /** Ikona braku możliwości uploadu*/
      fileUploadSuccess: <string>'draft',

      /** Ikona pokazywania więcej */
      expand: <string>'expand_more',

      /** Ikona pokazywana hasła */
      showPassword: <string>'visibility',

      /** Ikona ukrywania hasła */
      hidePassword: <string>'visibility_off'
    }
  },

  styles: {}
};

export default defaults;
